import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Webhook, Plus, Trash2, Pause, Play, RotateCw, Send, Check, X } from 'lucide-react';

const ACCENT = '#f97316';

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'paused' | 'disabled';
  consecutive_failures: number;
  failure_threshold: number;
  last_delivery_at: string | null;
  created_at: string;
  headers: Record<string, string>;
  retry_policy: { max_retries: number; backoff_multiplier: number; initial_delay_seconds: number };
  metadata: Record<string, string>;
}

const EVENT_GROUPS: Record<string, string[]> = {
  PSA: ['psa.ticket.created', 'psa.ticket.updated', 'psa.ticket.status_changed', 'psa.ticket.assigned', 'psa.time_entry.created', 'psa.company.created', 'psa.contact.created'],
  CRM: ['crm.contact.created', 'crm.contact.updated', 'crm.company.created', 'crm.deal.created', 'crm.deal.stage_changed'],
  Books: ['books.invoice.created', 'books.invoice.paid', 'books.payment.received'],
  CMDB: ['cmdb.ci.created', 'cmdb.ci.updated', 'cmdb.password.created'],
  Bridge: ['bridge.import.started', 'bridge.import.completed', 'bridge.import.failed'],
};

export default function WebhooksPage() {
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; status_code?: number } | null>(null);

  // Create form
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [allEvents, setAllEvents] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<{ key: string; value: string }[]>([]);
  const [showRetryConfig, setShowRetryConfig] = useState(false);
  const [maxRetries, setMaxRetries] = useState(5);
  const [backoffMultiplier, setBackoffMultiplier] = useState(2);
  const [initialDelay, setInitialDelay] = useState(10);
  const [creating, setCreating] = useState(false);
  const [urlError, setUrlError] = useState('');

  const loadWebhooks = useCallback(async () => {
    try {
      const data = await api.get<{ webhooks: WebhookEndpoint[] }>('/webhooks');
      setWebhooks(data.webhooks);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadWebhooks(); }, [loadWebhooks]);

  const validateUrl = async (value: string) => {
    if (!value) return;
    try {
      const res = await api.post<{ reachable: boolean; error?: string }>('/webhooks/validate-url', { url: value });
      setUrlError(res.reachable ? '' : (res.error || 'URL is not reachable'));
    } catch { /* skip */ }
  };

  const handleCreate = async () => {
    if (!name.trim() || !url.trim()) return;
    setCreating(true);
    try {
      const events = allEvents ? ['*'] : selectedEvents;
      const headers: Record<string, string> = {};
      for (const h of customHeaders) {
        if (h.key.trim()) headers[h.key.trim()] = h.value;
      }
      await api.post('/webhooks', {
        name: name.trim(),
        url: url.trim(),
        events,
        headers,
        retry_policy: { max_retries: maxRetries, backoff_multiplier: backoffMultiplier, initial_delay_seconds: initialDelay },
      });
      setShowCreate(false);
      resetCreateForm();
      loadWebhooks();
    } catch (err: any) {
      alert(err.message || 'Failed to create webhook');
    }
    setCreating(false);
  };

  const resetCreateForm = () => {
    setName(''); setUrl(''); setSelectedEvents([]); setAllEvents(false);
    setCustomHeaders([]); setMaxRetries(5); setBackoffMultiplier(2); setInitialDelay(10);
    setUrlError(''); setShowRetryConfig(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/webhooks/${id}`);
      setConfirmDelete(null);
      loadWebhooks();
    } catch { /* empty */ }
  };

  const handleTogglePause = async (wh: WebhookEndpoint) => {
    const newStatus = wh.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/webhooks/${wh.id}`, { status: newStatus });
      loadWebhooks();
    } catch { /* empty */ }
  };

  const handleRotateSecret = async (id: string) => {
    try {
      const res = await api.post<{ new_secret: string; old_secret: string; old_secret_expires_at: string }>(`/webhooks/${id}/rotate-secret`);
      alert(`New secret: ${res.new_secret}\n\nOld secret valid until: ${new Date(res.old_secret_expires_at).toLocaleString()}\n\nUpdate your endpoint now.`);
    } catch (err: any) {
      alert(err.message || 'Failed to rotate secret');
    }
  };

  const handleTestEvent = async (id: string) => {
    setTestingId(id);
    try {
      const res = await api.post<{ success: boolean; status_code?: number }>(`/webhooks/${id}/test`);
      setTestResult({ id, success: res.success, status_code: res.status_code });
      setTimeout(() => setTestResult(null), 3000);
    } catch { /* empty */ }
    setTestingId(null);
  };

  const toggleEvent = (event: string) => {
    setSelectedEvents(prev => prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]);
  };

  const statusColor = (s: WebhookEndpoint['status']) =>
    s === 'active' ? { bg: '#dcfce7', color: '#166534' } :
    s === 'paused' ? { bg: '#fef9c3', color: '#854d0e' } :
    { bg: '#fee2e2', color: '#991b1b' };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading webhooks...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Webhooks</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Receive real-time notifications when data changes across your connected systems</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnStyle}>
          <Plus size={16} /> Add Endpoint
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>Add Webhook Endpoint</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Production Webhook" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>URL (HTTPS required)</label>
                <input
                  value={url}
                  onChange={e => { setUrl(e.target.value); setUrlError(''); }}
                  onBlur={e => validateUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  style={{ ...inputStyle, borderColor: urlError ? '#ef4444' : '#d1d5db' }}
                />
                {urlError && <p style={{ color: '#ef4444', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>{urlError}</p>}
              </div>

              <div>
                <label style={labelStyle}>Events</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={allEvents} onChange={e => setAllEvents(e.target.checked)} />
                  Subscribe to all events (*)
                </label>
                {!allEvents && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.entries(EVENT_GROUPS).map(([group, events]) => (
                      <div key={group}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', margin: '0 0 0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{group}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {events.map(event => (
                            <button
                              key={event}
                              onClick={() => toggleEvent(event)}
                              style={{
                                padding: '0.2rem 0.5rem',
                                fontSize: '0.75rem',
                                borderRadius: '0.25rem',
                                border: '1px solid',
                                borderColor: selectedEvents.includes(event) ? ACCENT : '#d1d5db',
                                backgroundColor: selectedEvents.includes(event) ? ACCENT : '#fff',
                                color: selectedEvents.includes(event) ? '#fff' : '#374151',
                                cursor: 'pointer',
                              }}
                            >{event.split('.').slice(1).join('.')}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Custom Headers</label>
                  <button onClick={() => setCustomHeaders(prev => [...prev, { key: '', value: '' }])} style={{ fontSize: '0.75rem', color: ACCENT, background: 'none', border: 'none', cursor: 'pointer' }}>+ Add</button>
                </div>
                {customHeaders.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <input
                      placeholder="Header name"
                      value={h.key}
                      onChange={e => setCustomHeaders(prev => prev.map((x, j) => j === i ? { ...x, key: e.target.value } : x))}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <input
                      placeholder="Value"
                      value={h.value}
                      onChange={e => setCustomHeaders(prev => prev.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button onClick={() => setCustomHeaders(prev => prev.filter((_, j) => j !== i))} style={iconBtnStyle}><X size={14} /></button>
                  </div>
                ))}
              </div>

              <div>
                <button
                  onClick={() => setShowRetryConfig(!showRetryConfig)}
                  style={{ fontSize: '0.875rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {showRetryConfig ? '▲' : '▼'} Advanced: Retry Configuration
                </button>
                {showRetryConfig && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                    <div>
                      <label style={labelStyle}>Max Retries (default: 5)</label>
                      <input type="number" value={maxRetries} min={0} max={10} onChange={e => setMaxRetries(Number(e.target.value))} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Backoff Multiplier (default: 2)</label>
                      <input type="number" value={backoffMultiplier} min={1} max={10} step={0.5} onChange={e => setBackoffMultiplier(Number(e.target.value))} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Initial Delay (seconds, default: 10)</label>
                      <input type="number" value={initialDelay} min={1} max={3600} onChange={e => setInitialDelay(Number(e.target.value))} style={inputStyle} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreate(false); resetCreateForm(); }} style={{ ...btnStyle, backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>
              <button
                onClick={handleCreate}
                disabled={creating || !name.trim() || !url.trim() || (!allEvents && selectedEvents.length === 0)}
                style={{ ...btnStyle, opacity: creating ? 0.6 : 1 }}
              >
                {creating ? 'Creating...' : 'Create Webhook'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {webhooks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <Webhook size={32} color="#9ca3af" style={{ margin: '0 auto 0.75rem' }} />
          <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>No webhook endpoints yet</p>
          <button onClick={() => setShowCreate(true)} style={btnStyle}>Add Endpoint</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {webhooks.map(wh => {
            const sc = statusColor(wh.status);
            const testRes = testResult?.id === wh.id ? testResult : null;
            return (
              <div key={wh.id} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Webhook size={16} color="#9ca3af" style={{ marginTop: '0.125rem', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                      <button
                        onClick={() => navigate(`/webhooks/${wh.id}`)}
                        style={{ fontWeight: 600, fontSize: '0.875rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#111827', textDecoration: 'underline' }}
                      >{wh.name}</button>
                      <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: 500 }}>
                        {wh.status}
                      </span>
                      {wh.consecutive_failures > 0 && (
                        <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: 500 }}>
                          {wh.consecutive_failures} failures
                        </span>
                      )}
                    </div>
                    <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wh.url}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {wh.events.slice(0, 5).map(e => (
                        <span key={e} style={{ padding: '0.0625rem 0.375rem', fontSize: '0.6875rem', borderRadius: '0.25rem', border: '1px solid #e5e7eb', color: '#6b7280' }}>{e}</span>
                      ))}
                      {wh.events.length > 5 && (
                        <span style={{ padding: '0.0625rem 0.375rem', fontSize: '0.6875rem', borderRadius: '0.25rem', border: '1px solid #e5e7eb', color: '#9ca3af' }}>+{wh.events.length - 5} more</span>
                      )}
                    </div>
                    {wh.last_delivery_at && (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>Last delivery: {new Date(wh.last_delivery_at).toLocaleString()}</p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                    {/* Test */}
                    <button
                      onClick={() => handleTestEvent(wh.id)}
                      disabled={testingId === wh.id}
                      title="Send test event"
                      style={{ ...iconBtnStyle, color: testRes ? (testRes.success ? '#16a34a' : '#dc2626') : undefined }}
                    >
                      {testRes ? (testRes.success ? <Check size={14} /> : <X size={14} />) : <Send size={14} />}
                    </button>
                    {/* Pause/Resume */}
                    {wh.status !== 'disabled' && (
                      <button onClick={() => handleTogglePause(wh)} title={wh.status === 'active' ? 'Pause' : 'Resume'} style={iconBtnStyle}>
                        {wh.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                    )}
                    {/* Rotate Secret */}
                    <button onClick={() => handleRotateSecret(wh.id)} title="Rotate secret" style={iconBtnStyle}>
                      <RotateCw size={14} />
                    </button>
                    {/* Delete */}
                    {confirmDelete === wh.id ? (
                      <>
                        <button onClick={() => handleDelete(wh.id)} style={{ ...iconBtnStyle, color: '#dc2626' }} title="Confirm delete"><Check size={14} /></button>
                        <button onClick={() => setConfirmDelete(null)} style={iconBtnStyle} title="Cancel"><X size={14} /></button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(wh.id)} style={iconBtnStyle} title="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
  backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
};
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '2rem', height: '2rem', backgroundColor: 'transparent',
  border: '1px solid #e5e7eb', borderRadius: '0.375rem', cursor: 'pointer', color: '#6b7280',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
};
const modalStyle: React.CSSProperties = {
  backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
  width: '100%', maxWidth: '480px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem', fontSize: '0.875rem',
  border: '1px solid #d1d5db', borderRadius: '0.375rem', boxSizing: 'border-box',
};
